import React, {useEffect, useMemo, useState} from 'react';
import Input from '../Input';
import Select from 'react-select';
import Checkbox from '../Checkbox';
import {
  useAgencyWaterTestKitTypesQuery,
  useGetTimezonesQuery,
  useLazyGetAgencyQuery,
  useUpdateAgancyProfileSettingsMutation,
} from '../../store/builder/inspections.api';
import CustomSelect from '../CustomSelect';
import Cropper from 'react-cropper';
import {setLoader} from '../../store/builder/reducer';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import log from '../../SVG/Log';
import {getUser} from '../../store/builder/loginSlice';
import {useTranslation} from '../../hooks/useTranslation';
import Tooltip from '../Tooltip';

const timeOptions = [
  {
    label: '12:00 AM',
    value: 0,
  },
  {
    label: '1:00 AM',
    value: 60,
  },
  {
    label: '2:00 AM',
    value: 120,
  },
  {
    label: '3:00 AM',
    value: 180,
  },
  {
    label: '4:00 AM',
    value: 240,
  },
  {
    label: '5:00 AM',
    value: 300,
  },
  {
    label: '6:00 AM',
    value: 360,
  },
  {
    label: '7:00 AM',
    value: 420,
  },
  {
    label: '8:00 AM',
    value: 480,
  },
  {
    label: '9:00 AM',
    value: 540,
  },
  {
    label: '10:00 AM',
    value: 600,
  },
  {
    label: '11:00 AM',
    value: 660,
  },
  {
    label: '12:00 PM',
    value: 720,
  },
  {
    label: '1:00 PM',
    value: 780,
  },
  {
    label: '2:00 PM',
    value: 840,
  },
  {
    label: '3:00 PM',
    value: 900,
  },
  {
    label: '4:00 PM',
    value: 960,
  },
  {
    label: '5:00 PM',
    value: 1020,
  },
  {
    label: '6:00 PM',
    value: 1080,
  },
  {
    label: '7:00 PM',
    value: 1140,
  },
  {
    label: '8:00 PM',
    value: 1200,
  },
  {
    label: '9:00 PM',
    value: 1260,
  },
  {
    label: '10:00 PM',
    value: 1320,
  },
  {
    label: '11:00 PM',
    value: 1380,
  },
];

const FboProfile = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const {
    data: timezoneData,
    error: timezoneError,
    isLoading: timezoneIsLoading,
    isSuccess: timezoneIsSuccess,
    isError: timezoneIsError,
  } = useGetTimezonesQuery();
  const {
    data: waterKitData,
    error: waterKitError,
    isLoading: waterKitIsLoading,
    isSuccess: waterKitIsSuccess,
    isError: waterKitIsError,
  } = useAgencyWaterTestKitTypesQuery();
  const [nozzleScreenOptions, setNozzleScreenOptions] = useState([
    {value: 1, label: '1'},
    {value: 2, label: '2'},
    {value: 3, label: '3'},
  ]);
  const [
    updateAgency,
    {
      data: updateAgancyData,
      isError: updateAgancyIsError,
      isLoading: updateAgancyIsLoading,
      isSuccess: updateAgancyIsSuccess,
      error: updateAgancyError,
    },
  ] = useUpdateAgancyProfileSettingsMutation();

  const [phoneNumber, setPhoneNumber] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [chosenPhoto, setChosenPhoto] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [cropper, setCropper] = useState();
  const [changeData, setChangeData] = useState({});
  const [waterKitOptions, setWaterKitOptions] = useState([]);
  const isFieldDisabled = fieldKey =>
    userInfo?.agency?.disabled_fields?.includes(fieldKey);

  useEffect(() => {
    if (userInfo?.agency) {
      setChangeData({
        airport_id: userInfo?.agency?.airport_id,
        timezone: userInfo?.agency?.timezone,
        title: userInfo?.agency?.title,
        start_day_minutes: userInfo?.agency?.start_day_minutes,
        has_upstream_tests: userInfo?.agency?.has_upstream_tests ? 1 : 0,
        has_corrected_dp: userInfo?.agency?.has_corrected_dp ? 1 : 0,
        water_test_kit_id: userInfo?.agency?.water_test_kit_id,
        daily_weekly_grace_period: userInfo?.agency?.daily_weekly_grace_period,
        nozzle_screen_cleaning_method:
          userInfo?.agency?.nozzle_screen_cleaning_method,
        has_hydrant_systems: userInfo?.agency?.has_hydrant_systems ? 1 : 0,
        allow_daily_dec_equipment: userInfo?.agency?.allow_daily_dec_equipment
          ? 1
          : 0,
        allow_generating_decommissioned: userInfo?.agency
          ?.allow_generating_decommissioned
          ? 1
          : 0,
        partial_inspections_notification: userInfo?.agency
          ?.partial_inspections_notification
          ? 1
          : 0,
        partial_inspections_notification_minutes:
          userInfo?.agency?.partial_inspections_notification_minutes,
        upcoming_oos_notification_time:
          userInfo?.agency?.upcoming_oos_notification_time,
        is_settings_complete: userInfo?.agency?.is_settings_complete,
        inactivity_timeout_mobile:
          userInfo?.agency?.inactivity_timeout_mobile ?? 0,
        inactivity_timeout_web: userInfo?.agency?.inactivity_timeout_web ?? 0,
        is_pin_enabled: userInfo?.agency?.is_pin_enabled ? 1 : 0,
        pin_require_seconds: userInfo?.agency?.pin_require_seconds ?? null,
        area_director: userInfo?.agency?.area_director,
        svp: userInfo?.agency?.svp,
      });
      setPhoneNumber(userInfo?.agency?.fbo_phone?.replace(/\D/g, ''));
    }
  }, [userInfo]);

  useEffect(() => {
    if (phoneNumber) {
      setChangeData(old => ({...old, fbo_phone: phoneNumber}));
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (updateAgancyIsLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [updateAgancyIsLoading]);

  useEffect(() => {
    if (waterKitData?.length > 0) {
      setWaterKitOptions(
        waterKitData?.map(item => ({label: item?.name, value: item?.id})),
      );
    }
  }, [waterKitData]);

  useEffect(() => {
    if (typeof chosenPhoto === 'string') {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [chosenPhoto]);

  const handleInput = e => {
    setChangeData(old => ({...old, [e.target.name]: e.target.value}));
  };

  const handleUpcomingOosNotificationTime = e => {
    setChangeData(old => ({...old, upcoming_oos_notification_time: e.value}));
  };

  const handleTimezone = e => {
    setChangeData(old => ({...old, timezone: e.value}));
  };
  const handleChange = event => {
    let formattedPhone = event.target.value.replace(/\D/g, ''); // remove non-numeric characters
    formattedPhone = formattedPhone.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '($1) $2-$3',
    );
    setPhoneNumber(formattedPhone);
  };
  const handleStartTime = e => {
    setChangeData(old => ({...old, start_day_minutes: e.value}));
  };

  const handleWaterKit = e => {
    setChangeData(old => ({...old, water_test_kit_id: e.target.value}));
  };

  const handleUpstream = e => {
    setChangeData(old => ({
      ...old,
      has_upstream_tests: e.target.checked === true ? 1 : 0,
    }));
  };
  const handleHasCorrected = e => {
    setChangeData(old => ({
      ...old,
      has_corrected_dp: e.target.checked === true ? 1 : 0,
    }));
  };

  const handleHasHydrantSystems = e => {
    setChangeData(old => ({
      ...old,
      has_hydrant_systems: e.target.checked === true ? 1 : 0,
    }));
  };

  const handleAllowDailyDecEquipment = e => {
    setChangeData(old => ({
      ...old,
      allow_daily_dec_equipment: e.target.checked === true ? 1 : 0,
    }));
  };

  const handleAllowGeneratingDecommissioned = e => {
    setChangeData(old => ({
      ...old,
      allow_generating_decommissioned: e.target.checked === true ? 1 : 0,
    }));
  };

  const handlePartialInspectionsNotification = e => {
    setChangeData(old => ({
      ...old,
      partial_inspections_notification: e.target.checked === true ? 1 : 0,
    }));
  };

  const handlePartialInspectionsNotificationMinutes = e => {
    setChangeData(old => ({
      ...old,
      partial_inspections_notification_minutes: e.value,
    }));
  };

  const cropperHandler = e => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setChosenPhoto(reader.result);
    };
    reader.readAsDataURL(files[0]);
  };

  function base64ToBlob(base64Data) {
    const contentType = base64Data?.substring(5, base64Data?.indexOf(';'));
    const byteCharacters = atob(
      base64Data?.substring(base64Data?.indexOf(',') + 1),
    );
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, {type: contentType});
  }

  const getCropData = () => {
    if (typeof cropper !== 'undefined') {
      setPhoto(cropper.getCroppedCanvas().toDataURL());
      setShowModal(false);
    }
  };

  const updateFbo = () => {
    const formData = new FormData();
    if (photo) {
      formData.append('logo', base64ToBlob(photo));
    }
    formData.append('title', changeData?.title);
    formData.append('timezone', changeData?.timezone);
    formData.append('airport_id', changeData?.airport_id);
    formData.append('start_day_minutes', changeData?.start_day_minutes);
    formData.append('has_upstream_tests', changeData?.has_upstream_tests);
    formData.append('has_corrected_dp', changeData?.has_corrected_dp);
    formData.append('water_test_kit_id', changeData?.water_test_kit_id);
    formData.append(
      'daily_weekly_grace_period',
      changeData?.daily_weekly_grace_period,
    );
    formData.append(
      'nozzle_screen_cleaning_method',
      changeData?.nozzle_screen_cleaning_method,
    );
    formData.append('has_hydrant_systems', changeData?.has_hydrant_systems);
    formData.append(
      'allow_daily_dec_equipment',
      changeData?.allow_daily_dec_equipment,
    );
    formData.append(
      'allow_generating_decommissioned',
      changeData?.allow_generating_decommissioned,
    );
    formData.append(
      'partial_inspections_notification',
      changeData?.partial_inspections_notification,
    );
    formData.append(
      'partial_inspections_notification_minutes',
      changeData?.partial_inspections_notification_minutes,
    );
    formData.append(
      'upcoming_oos_notification_time',
      changeData?.upcoming_oos_notification_time,
    );
    formData.append(
      'inactivity_timeout_mobile',
      changeData?.inactivity_timeout_mobile ?? 0,
    );
    formData.append(
      'inactivity_timeout_web',
      changeData?.inactivity_timeout_web ?? 0,
    );
    formData.append('is_pin_enabled', changeData?.is_pin_enabled ? 1 : 0);
    if (changeData?.is_pin_enabled) {
      formData.append('pin_require_seconds', changeData?.pin_require_seconds ?? '');
    }
    formData.append('area_director', changeData?.area_director || '');
    formData.append('svp', changeData?.svp || '');

    if (
      Number(changeData?.daily_weekly_grace_period) > -1 &&
      Number(changeData?.daily_weekly_grace_period) <= 365
    ) {
      updateAgency(formData)
        .then(res => {
          if (res?.error) {
            toast.error(res?.error?.data?.message);
          } else {
            toast.success(res?.data?.message);
            if (!changeData?.is_settings_complete) {
              setShowSaveModal(true);
            }
          }
        })
        .finally(() => {
          dispatch(getUser());
        });
    } else {
      toast.error(t('fboProfile.messages.dailyWeeklyGracePeriodError'));
    }
  };

  const renderNozzleScreenDropdown = useMemo(() => {
    return (
      <div style={{width: 630}}>
        <CustomSelect
          label={t('fboProfile.fields.nozzleScreenCleaningMethod')}
          disabled={isFieldDisabled('nozzle_screen_cleaning_method')}
          options={nozzleScreenOptions}
          name={'nozzle_screen_cleaning_method'}
          onChange={handleInput}
          defaultValue={
            userInfo?.agency
              ? nozzleScreenOptions?.filter(
                  el =>
                    el.value == userInfo?.agency?.nozzle_screen_cleaning_method,
                )[0]
              : null
          }
        />
        <label className='customInput'>
          <span
            className='customSelect__label'
            style={{
              marginBottom: 8,
            }}
          >
            {t('fboProfile.fields.upcomingOosNotificationTime')}
            <Tooltip
              tooltipText={t(
                'fboProfile.fields.upcomingOosNotificationTimeTooltip',
              )}
            />
          </span>
          <Select
            options={timeOptions}
            disabled={isFieldDisabled('upcoming_oos_notification_time')}
            name={'upcoming_oos_notification_time'}
            value={timeOptions?.filter(
              item =>
                item?.value === changeData?.upcoming_oos_notification_time,
            )}
            onChange={handleUpcomingOosNotificationTime}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 629,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
        </label>
      </div>
    );
  }, [userInfo?.agency, changeData]);

  return (
    <div>
      {showSaveModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p></p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => setShowSaveModal(false)}
              />
            </div>
            <div className='modal__content-body'>
              <div className='modal__content-body save-modal'>
                <img src='/assets/icons/Done.svg' alt='img' />
                <span>{t('fboProfile.modals.setupComplete.message')}</span>
              </div>
            </div>
            <div className='modal__content-bottom'>
              <button
                className='save full'
                onClick={() => setShowSaveModal(false)}
              >
                {t('fboProfile.modals.setupComplete.gotIt')}
              </button>
            </div>
          </div>
          <div className='modal__bg'></div>
        </div>
      )}
      {showModal && (
        <div className='p-modal'>
          <div className='p-modal__content'>
            <div className='p-modal__header'>
              <p></p>
              <h5 className='p-modal__title'>
                {t('fboProfile.modals.cropImage.title')}
              </h5>
              <div className='p-modal__close'>
                <img
                  src='/assets/icons/close.svg'
                  alt='close'
                  onClick={() => {
                    setShowModal(false);
                    setChosenPhoto(null);
                  }}
                />
              </div>
            </div>
            <div className='p-modal__cropper'>
              <Cropper
                style={{
                  height: 400,
                  width: '100%',
                }}
                zoomTo={0}
                initialAspectRatio={1}
                preview='.img-preview'
                src={chosenPhoto}
                aspectRatio={1}
                viewMode={0}
                minCropBoxHeight={300}
                minCropBoxWidth={300}
                background={false}
                responsive={true}
                autoCropArea={1}
                checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                onInitialized={instance => {
                  setCropper(instance);
                }}
                // minCropBoxWidth={300}
                guides={true}
              />
            </div>
            <div className='p-modal__btns'>
              <label
                htmlFor='profile_photo'
                className='profile__imageBox-upload'
              >
                {t('fboProfile.modals.cropImage.changePhoto')}
              </label>
              <div className='profile__imageBox-upload' onClick={getCropData}>
                {t('fboProfile.modals.cropImage.savePhoto')}
              </div>
            </div>
          </div>
          <div className='p-modal__bg'></div>
        </div>
      )}
      <div
        className='profile__info profile'
        style={{pointerEvents: userInfo?.is_sso_auth ? 'none' : 'auto'}}
      >
        <div className='profile__info column'>
          <p className='profile__title'>{t('fboProfile.title')}</p>
          <div className='profile__imageBox'>
            <img
              className='profile__avatar'
              src={photo ? photo : userInfo?.agency?.logo}
              alt='avatar'
            />
            {!userInfo?.is_sso_auth && (
              <div>
                <p className='profile__imageBox-desc'>
                  {t('fboProfile.imageUpload.description')}
                </p>
                <label
                  htmlFor='profile_photo'
                  className='profile__imageBox-label'
                >
                  <input
                    type='file'
                    name='profile_photo'
                    id='profile_photo'
                    onChange={cropperHandler}
                    accept='image/jpeg, image/jpg, image/png'
                  />
                  {t('fboProfile.imageUpload.button')}
                </label>
              </div>
            )}
          </div>
        </div>
        <Input
          label={t('fboProfile.fields.fboName')}
          disabled={isFieldDisabled('title')}
          maxwidth={595}
          placeholder={t('fboProfile.fields.fboNamePlaceholder')}
          id={'fbo_phone'}
          name={'title'}
          defaultValue={changeData?.title}
          onChange={handleInput}
        />
        <label className='customInput'>
          <span style={{marginBottom: 8}}>
            {t('fboProfile.fields.timezone')}
          </span>
          <Select
            value={
              changeData?.timezone
                ? {
                    value: changeData?.timezone,
                    label: changeData?.timezone,
                  }
                : null
            }
            options={timezoneData?.data?.map(item => ({
              value: item,
              label: item,
            }))}
            onChange={handleTimezone}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 629,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
        </label>
        <Input
          label={t('fboProfile.fields.airportId')}
          maxwidth={595}
          placeholder={t('fboProfile.fields.airportIdPlaceholder')}
          defaultValue={changeData?.airport_id}
          onChange={handleInput}
          disabled={isFieldDisabled('airport_id')}
          name={'airport_id'}
        />
        <p className='profile__title'>
          {t('fboProfile.fields.fboSettings')}
          {!changeData?.is_settings_complete && (
            <span className='required'> {t('fboProfile.fields.required')}</span>
          )}
        </p>
        <div style={{width: 630}}>
          <span
            className='customSelect__label'
            style={{
              marginBottom: 8,
            }}
          >
            {t('fboProfile.fields.inspectionGenerationLabel')}
            <Tooltip
              tooltipText={t('fboProfile.fields.inspectionGenerationTooltip')}
            />
          </span>
          <Select
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 629,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
            options={timeOptions}
            isDisabled={isFieldDisabled('start_day_minutes')}
            name={'start_day_minutes'}
            onChange={handleStartTime}
            value={timeOptions?.filter(
              item => +item?.value === +changeData?.start_day_minutes,
            )}
          />
        </div>
        {waterKitOptions?.length > 0 && (
          <div style={{width: 630}}>
            <CustomSelect
              label={t('fboProfile.fields.waterTestKit')}
              options={waterKitOptions}
              name={'water_test_kit_id'}
              onChange={handleWaterKit}
              disabled={isFieldDisabled('water_test_kit_id')}
              defaultValue={
                changeData?.water_test_kit_id
                  ? waterKitOptions?.filter(
                      el =>
                        el.value == changeData?.water_test_kit_id?.toString(),
                    )[0]
                  : null
              }
            />
          </div>
        )}
        {renderNozzleScreenDropdown}
        <div className='facility-modal__field' style={{marginTop: 16}}>
          <Checkbox
            disabled={isFieldDisabled('has_upstream_tests')}
            key={'has_upstream_tests'}
            label={t('fboProfile.fields.hasUpstreamTests')}
            id={'has_upstream_tests'}
            checked={changeData?.has_upstream_tests}
            onChange={handleUpstream}
          />
        </div>
        <div className='facility-modal__field'>
          <Checkbox
            disabled={isFieldDisabled('has_corrected_dp')}
            key={'has_corrected_dp'}
            label={t('fboProfile.fields.hasCorrectedDp')}
            id={'has_corrected_dp'}
            checked={changeData?.has_corrected_dp}
            onChange={handleHasCorrected}
          />
        </div>
        <div className='facility-modal__field'>
          <Checkbox
            checked={changeData?.has_hydrant_systems}
            key={'has_hydrant_systems'}
            label={t('fboProfile.fields.hasHydrantSystems')}
            id={'has_hydrant_systems'}
            onChange={handleHasHydrantSystems}
            disabled={isFieldDisabled('has_hydrant_systems')}
          />
        </div>
        <div className='facility-modal__field'>
          <Checkbox
            disabled={isFieldDisabled('allow_daily_dec_equipment')}
            checked={changeData?.allow_daily_dec_equipment}
            key={'allow_daily_dec_equipment'}
            label={t('fboProfile.fields.allowDailyDecEquipment')}
            id={'allow_daily_dec_equipment'}
            onChange={handleAllowDailyDecEquipment}
          />
        </div>
        <div className='facility-modal__field'>
          <Checkbox
            disabled={isFieldDisabled('allow_generating_decommissioned')}
            checked={changeData?.allow_generating_decommissioned}
            key={'allow_generating_decommissioned'}
            label={t('fboProfile.fields.allowGeneratingDecommissioned')}
            id={'allow_generating_decommissioned'}
            onChange={handleAllowGeneratingDecommissioned}
          />
        </div>
        <div className='facility-modal__field'>
          <Checkbox
            disabled={isFieldDisabled('partial_inspections_notification')}
            checked={!!changeData?.partial_inspections_notification}
            key={'partial_inspections_notification'}
            label={t('fboProfile.fields.partialInspectionsNotification')}
            id={'partial_inspections_notification'}
            onChange={handlePartialInspectionsNotification}
          />
        </div>
        <div style={{width: 630, marginTop: 8}}>
          <label className='customInput'>
            <span
              className='customSelect__label'
              style={{
                marginBottom: 8,
              }}
            >
              {t('fboProfile.fields.partialInspectionsNotificationMinutes')}
            </span>
            <Select
              options={timeOptions}
              isDisabled={
                isFieldDisabled('partial_inspections_notification_minutes') ||
                !changeData?.partial_inspections_notification
              }
              name={'partial_inspections_notification_minutes'}
              value={timeOptions?.filter(
                item =>
                  item?.value ===
                  changeData?.partial_inspections_notification_minutes,
              )}
              onChange={handlePartialInspectionsNotificationMinutes}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 629,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
            />
          </label>
        </div>
        <div style={{height: 16}}></div>
        <Input
          disabled={isFieldDisabled('daily_weekly_grace_period')}
          label={t('fboProfile.fields.dailyWeeklyGracePeriod')}
          maxwidth={595}
          placeholder={t('fboProfile.fields.dailyWeeklyGracePeriodPlaceholder')}
          defaultValue={changeData?.daily_weekly_grace_period}
          value={changeData?.daily_weekly_grace_period}
          onChange={handleInput}
          name={'daily_weekly_grace_period'}
          type={'number'}
          tooltip={true}
          tooltipText={t('fboProfile.fields.dailyWeeklyGracePeriodTooltip')}
        />
        {/* area_director and svp */}
        {!userInfo?.agency?.hidden_fields?.includes('area_director') && (
          <Input
            disabled={isFieldDisabled('area_director')}
            label={t('fboProfile.fields.areaDirector')}
            maxwidth={595}
            placeholder={t('fboProfile.fields.areaDirectorPlaceholder')}
            defaultValue={changeData?.area_director}
            value={changeData?.area_director}
            onChange={handleInput}
            name={'area_director'}
          />
        )}
        {!userInfo?.agency?.hidden_fields?.includes('svp') && (
          <Input
            disabled={isFieldDisabled('svp')}
            label={t('fboProfile.fields.svp')}
            maxwidth={595}
            placeholder={t('fboProfile.fields.svpPlaceholder')}
            defaultValue={changeData?.svp}
            value={changeData?.svp}
            onChange={handleInput}
            name={'svp'}
          />
        )}
      </div>
      <button className='profile__methods-btn' onClick={updateFbo}>
        {t('actions.save')}
      </button>
    </div>
  );
};

export default FboProfile;
